import { SessionDayType } from '@shared/types';
import { getHolidayName, getIsSaturday, getIsWeekend, getPremiumDayType } from '@shared/utils';
import { Box, Text } from 'grommet';
import { formatDateOnly } from '@talon/web/src/utils/dateOnly';
import { StatusPill } from '../StatusPill';
import { TalonTip } from '../TalonTip';

export interface IPremiumDayPillProps {
  /** The session's date ("YYYY-MM-DD") */
  date?: string | null;
}

/** What a session's date means for its pay, in words, or undefined on an ordinary day */
export const getPremiumDayTip = (date: string): string | undefined => {
  const day = getPremiumDayType(date);
  if (!day) return undefined;

  const isHoliday = day === SessionDayType.Holiday;
  const name = isHoliday ? getHolidayName(date) : day;
  const check = `If this session isn't on a ${isHoliday ? 'holiday' : name}, check its date.`;

  if (isHoliday && getIsWeekend(date)) {
    // A holiday on a weekend owes both premiums, the holiday one doubling the weekend rate
    const weekend = getIsSaturday(date) ? 'Saturday' : 'Sunday';
    return `${name} falls on a ${weekend}, so work on it is paid both a ${weekend} premium and a holiday premium that doubles the ${weekend} rate. ${check}`;
  }
  if (isHoliday) return `${name} is a holiday, so work on it is paid a holiday premium (straight time again). ${check}`;
  return `Work on a ${name} is paid a ${name} premium. ${check}`;
};

/**
 * Marks a session on a holiday or a weekend (#181). Work on one is paid a premium, which can be a surprise when the
 * date was picked by accident, so it's named where the date is set.
 */
export const PremiumDayPill = ({ date }: IPremiumDayPillProps) => {
  const day = date ? getPremiumDayType(date) : undefined;
  const tip = date ? getPremiumDayTip(date) : undefined;
  if (!date || !day || !tip) return null;

  const isHoliday = day === SessionDayType.Holiday;
  const name = isHoliday ? getHolidayName(date) : day;

  return (
    <TalonTip
      content={
        <Text size="small">
          The session&apos;s date is {formatDateOnly(date)}. {tip}
        </Text>
      }
    >
      <Box pad={{ bottom: '8px' }} flex={false}>
        <StatusPill label={`${isHoliday ? 'Holiday' : 'Weekend'}: ${name}`} tone="warning" />
      </Box>
    </TalonTip>
  );
};
